/* eslint-disable max-lines */
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { color } from 'styles/colors'
import { screenSize, NoData } from 'styles/shared'
import {
  headerProps,
  sysConfigComponentProps,
  GridProps,
  ExpandCollabsableProp,
  TextProps
} from './types'
import { Panel } from 'components/Panel'
import ComponentList from './ComponentList'
import LastUpdated from 'pages/Device/LastUpdated'
import useNexiaStream from 'hooks/useNexiaStream'
import * as FRIENDLY_NAMES from 'helpers/reportFriendlyNames'
import {
  friendlyName,
  shortDateTime
} from 'helpers/formatters'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import { useWebsocketContext } from 'hooks/WebsocketContext'

import ExtendedDisconnect from 'pages/shared/Device/ExtendedDisconnect'

const ConfigComponents = ({
  deviceId,
  reportCache
}: sysConfigComponentProps) => {
  const { eventData: systemConfigreport } = useNexiaStream(
    deviceId,
    'sys_config',
    reportCache
  )

  const { eventData: systemComponentReport } = useNexiaStream(
    deviceId,
    'sys_components',
    reportCache
  )

  const { enabledFeatures } = useFeaturesContext()
  const { subscribe, unsubscribe, payload: communicatingComponentsReport } = useWebsocketContext(deviceId, 'communicating_components')
  const { payload: wsSystemConfigReport } = useWebsocketContext(deviceId, 'system_config')
  const isWebsocketEnabled = enabledFeatures.includes('montblanc_websockets')

  const CardTitle = ({ title, lastUpdated, children }: headerProps) => (
    <div>
      <S.StyledHeader3 fontWeight={'600'}>
        {title} {children ?? null}
      </S.StyledHeader3>
      {lastUpdated && (
        <S.StyledText >{`Last updated: ${lastUpdated ? shortDateTime(lastUpdated) : 'UNKNOWN'}`}</S.StyledText>
      )}
    </div>
  )

  const actualSystemConfigReport = isWebsocketEnabled
    ? wsSystemConfigReport?.systemConfig
    : systemConfigreport

  const actualCommunicatingComponentsReport = isWebsocketEnabled
    ? communicatingComponentsReport
    : systemComponentReport

  const extendedDisconnectComponents = isWebsocketEnabled ? communicatingComponentsReport?.extendedDisconnect : false
  const extendedDisconnectConfig = isWebsocketEnabled ? wsSystemConfigReport?.extendedDisconnect : false

  const installedAccessories =
    ['humidifier', 'ventilator', 'dehumidification'].filter(
      (key) =>
        actualSystemConfigReport &&
        actualSystemConfigReport[key] &&
        actualSystemConfigReport[key].toLowerCase() === 'installed'
    ).length || 0

  const systemComponentsCount = actualCommunicatingComponentsReport?.components?.length || 0

  const outdoorUnits = [{
    id: 'Type',
    data: actualSystemConfigReport?.outdoorUnitType || 'N/A',
    type: FRIENDLY_NAMES.OUTDOOR_UNIT_TYPE
  }, {
    id: 'Stage',
    data: actualSystemConfigReport?.outdoorStage || 'N/A',
    type: FRIENDLY_NAMES.OUTDOOR_STAGES
  }, {
    id: 'Compressor',
    data: actualSystemConfigReport?.compressorType || 'N/A',
    type: FRIENDLY_NAMES.COMPRESSOR_TYPE
  }]

  const indoorUnits = [{
    id: 'Type',
    data: actualSystemConfigReport?.indoorUnitType || 'N/A',
    type: FRIENDLY_NAMES.INDOOR_UNIT_TYPE
  }, {
    id: 'Stage',
    data: actualSystemConfigReport?.indoorStage || 'N/A',
    type: FRIENDLY_NAMES.INDOOR_STAGES
  }, {
    id: 'Blower',
    data: actualSystemConfigReport?.blowerType || 'N/A',
    type: FRIENDLY_NAMES.BLOWER_TYPE
  }]

  useEffect(() => {
    subscribe(deviceId, 'communicating_components')
    subscribe(deviceId, 'system_config')

    return () => {
      unsubscribe(deviceId, 'communicating_components')
      unsubscribe(deviceId, 'system_config')
    }
  }, [deviceId, subscribe, unsubscribe])

  return (
    <S.Panel isDashboard >
      {extendedDisconnectConfig ? (
        <ExtendedDisconnect title="Configuration" type="preview" />
      ) : (
        actualSystemConfigReport ? (
          <>
            <Panel.Header>
              <Panel.Title>Configuration</Panel.Title>
              <LastUpdated date={actualSystemConfigReport.lastUpdatedAt} />
            </Panel.Header>
            <S.UnitsContainer rows={1} columns={2}>
              <S.StyledHeader3 fontWeight={'600'} marginBottom={10}>OD Unit</S.StyledHeader3>
              <S.StyledHeader3 fontWeight={'600'} marginBottom={10}>ID Unit</S.StyledHeader3>
              <S.Units>
                {outdoorUnits.map((item) => (
                  <S.UnitData key={item.id} >
                    <S.StyledText style={{ textOverflow: 'unset' }}> {item.id}:</S.StyledText>
                    <S.StyledText style={{ textIndent: 5 }}>{friendlyName(item.data, item.type)}</S.StyledText>
                  </S.UnitData>
                ))}
              </S.Units>
              <S.Units >
                {indoorUnits.map((item) => (
                  <S.UnitData key={item.id} >
                    <S.StyledText style={{ textOverflow: 'unset' }}>{item.id}:</S.StyledText>
                    <S.StyledText style={{ textIndent: 5 }}>{friendlyName(item.data, item.type)}</S.StyledText>
                  </S.UnitData>
                ))}
              </S.Units>
            </S.UnitsContainer>
            <S.TopGap />
            <CardTitle title={'Accessories'} > <span data-testid="accessoriesInstalled">{`(${installedAccessories ?? 0})`}</span> </CardTitle>
            <S.TopGap />
            <CardTitle title={'Lockouts'} />
          </>
        ) : (
          <>
            <Panel.Header>
              <Panel.Title>Configuration</Panel.Title>
            </Panel.Header>
            <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          </>
        )
      )}
      <S.TopDivider />
      <S.ComponentsDiv height={250}>
        {
          extendedDisconnectComponents ? (
            <ExtendedDisconnect title="Components" type="preview" />
          ) : (
            actualCommunicatingComponentsReport ? (
              <>
                <Panel.Header>
                  <Panel.Title>Components {systemComponentsCount ? `(${systemComponentsCount})` : null}</Panel.Title>
                  <LastUpdated date={actualCommunicatingComponentsReport.lastUpdatedAt} />
                </Panel.Header>
                <S.TopGap />
                <ComponentList
                  header={['Description', 'Model', 'Warranty']}
                  list={actualCommunicatingComponentsReport?.components || []}
                />
              </>
            ) : (
              <>
                <Panel.Header>
                  <Panel.Title>Components</Panel.Title>
                </Panel.Header>
                <S.TopGap />
                <S.NoData><h1>No Data Received Yet</h1></S.NoData>
              </>
            )
          )
        }
      </S.ComponentsDiv>
    </S.Panel>
  )
}

export default ConfigComponents

const S = {
  Panel: styled(Panel).withConfig({
    shouldForwardProp: (prop) => !['size', 'textColor', 'fontWeight', 'marginBottom', 'isDashboard'].includes(prop)
  }) <{ size?: number, textColor?: string, isDashboard: boolean, fontWeight?: string, marginBottom?: number }>`
    scroll-margin-top: 60px;
    background-color: ${color('paneBackground')};
    border-bottom: ${({ isDashboard }) => isDashboard ? '0' : ''};
    padding: ${({ isDashboard }) => isDashboard ? '0px' : '20px 0px'};
    width: ${({ isDashboard }) => isDashboard ? 'calc(100% - 0px);' : 'calc(100% - 40px);'};
  
    p,h3 {
      font-family: Inter, sans-serif;
      text-transform: unset;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 1px;
    }`,

  UnitsContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['columns', 'gap', 'rows'].includes(prop)
  }) <GridProps>`
    display: grid;
    grid-template-rows: repeat(${(props) => props.rows ?? 1}, 1fr);
    grid-template-columns: repeat(${(props) => props.columns ?? 1}, 1fr);
    gap: ${(props) => props.gap ?? '5px'};
    padding: 2px;
    width: calc(100% - 9vw);
    p {
      flex: 1;
      margin-bottom: 10px;
    }
    @media ${screenSize.large} {
      width: calc(100% - 27vw);
    }`,

  Units: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
  `,

  UnitData: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    p {
      flex: unset;
      width: fit-content;
    }`,

  StyledText: styled.p.withConfig({
    shouldForwardProp: (prop) => !['size', 'textColor', 'fontWeight', 'marginBottom'].includes(prop)
  }) <TextProps>`
      color: ${({ textColor }) => color(textColor || 'DashboardConfigText')};
      font-size: ${({ size }) => size || 12}px;
      font-weight: ${({ fontWeight }) => fontWeight || 'unset'};
      margin-bottom: ${({ marginBottom }) => marginBottom || '1'}px; !important`,

  StyledHeader3: styled.h3.withConfig({
    shouldForwardProp: (prop) => !['size', 'textColor', 'fontWeight', 'marginBottom'].includes(prop)
  }) <TextProps>`
      color: ${({ textColor }) => color(textColor || 'primaryText')};
      font-size: ${({ size }) => size || 14}px;
      font-weight: ${({ fontWeight }) => fontWeight || 'unset'};
      margin-bottom: ${({ marginBottom }) => marginBottom || '1'}px !important;
      span {
        letter-spacing: 1px;
      }`,

  ComponentsDiv: styled.div<ExpandCollabsableProp>`
    max-height: ${(props) => props.height ?? 30}px;
    @media ${screenSize.small} {
      max-height: max-content;
    }
  `,

  Lockouts: styled.div``,
  TopDivider: styled.div`
    border-top-width: 2px;
    border-top-style: solid;
    border-top-color: ${color('borderTop')};
    margin: 12px 2px;
  `,
  TopGap: styled.div`
    margin: 14px 2px 2px 2px;
  `,
  NoData: styled(NoData)`
    padding : 50px 0 50px 0px;
    h1 {
      font-size: 24px;
    }
  `
}
